/** Типы ответов API — общие для сервера и мини-приложения. */
import { z } from 'zod';
import type { Permission, Role } from './roles.ts';
import { ROLES } from './roles.ts';
import { ProceduresInput, type CheckDto, type Procedure } from './procedures.ts';

export type EmployeeStatus = 'active' | 'blocked';

/**
 * Сотрудник из списка. Войти в приложение может только тот, кто в списке:
 * администратор указывает Telegram ID, @username или телефон, и при первом входе запись привязывается к Telegram.
 */
export interface EmployeeDto {
  id: string;
  /** Telegram ID; null — ещё не входил и задан только @username или телефон. */
  userId: number | null;
  fullName: string;
  /** Должность (название) — из справочника должностей. */
  position: string | null;
  positionId: string | null;
  /** Роль в приложении: администратор или сотрудник. */
  role: Role;
  status: EmployeeStatus;
  areaIds: string[];
  phone: string | null;
  /** @username из списка (без @) или текущий из Telegram после входа. */
  tgUsername: string | null;
  /** Последний вход в приложение; null — ещё не входил. */
  lastSeenAt: string | null;
  createdAt: string;
}

/** Кто открывал приложение, но его нет в списке. Администратор может добавить его одной кнопкой. */
export interface LoginAttemptDto {
  userId: number;
  firstName: string;
  lastName: string | null;
  username: string | null;
  lastSeenAt: string;
}

export interface MeDto {
  user: { id: number; firstName: string; lastName: string | null; username: string | null };
  /** null — человека нет в списке сотрудников: вход закрыт. */
  employee: EmployeeDto | null;
  permissions: Permission[];
  /** Руководитель: администратор или у должности есть подчинённые — ставит задачи и задание на смену. */
  manager: boolean;
  /** Для администратора: сколько людей не из списка пытались войти за неделю. */
  loginAttempts: number;
}

export type AreaKind = 'site' | 'shop' | 'line' | 'cell';
export const AREA_KIND_LABEL: Record<AreaKind, string> = { site: 'Площадка', shop: 'Цех', line: 'Линия', cell: 'Участок' };

export interface AreaDto {
  id: string;
  parentId: string | null;
  name: string;
  kind: AreaKind;
  sort: number;
}

/** Состояние связи с панелью по последней проверке. */
export interface PanelLink {
  ok: boolean | null;
  checkedAt: string | null;
  rttMs: number | null;
  error: string | null;
}

export type PanelSide = 'front' | 'back' | 'left' | 'right';
export const PANEL_SIDES: readonly PanelSide[] = ['front', 'back', 'left', 'right'];
export const PANEL_SIDE_LABEL: Record<PanelSide, string> = { front: 'спереди', back: 'сзади', left: 'слева', right: 'справа' };

/**
 * Пометка «спереди / сзади …» — только когда у машины больше одной панели (одна панель — без пометок).
 * panels — все известные панели (чтобы сосчитать соседей по той же машине).
 */
export function panelSideNote(p: Pick<PanelDto, 'id' | 'equipmentId' | 'side'>, panels: readonly Pick<PanelDto, 'id' | 'equipmentId'>[]): string | null {
  if (!p.side || !p.equipmentId) return null;
  const n = panels.filter((x) => x.equipmentId === p.equipmentId).length;
  return n > 1 ? PANEL_SIDE_LABEL[p.side] : null;
}

export interface PanelDto {
  id: string;
  areaId: string | null;
  /** Единица оборудования, к которой относится панель. */
  equipmentId: string | null;
  /** С какой стороны машины (если панелей у машины несколько). */
  side: PanelSide | null;
  name: string;
  description: string | null;
  /** Можно ли этому пользователю управлять (право роли и флаг панели). */
  canControl: boolean;
  /** Разрешено ли управление на самой панели (настройка администратора). */
  controlAllowed: boolean;
  enabled: boolean;
  sort: number;
  link: PanelLink;
}

/** Для администратора: адрес и признак пароля. Сам пароль наружу не отдаётся никогда. */
export interface PanelAdminDto extends PanelDto {
  host: string;
  port: number;
  hasPassword: boolean;
}

export type VncMode = 'view' | 'control';

export interface VncTicketDto {
  ticket: string;
  /** Номер сеанса в журнале — к нему приложение прикладывает снимки экрана в момент нажатий. */
  sessionId: string;
  /** Путь WebSocket относительно сайта: /vnc/<ticket>. */
  path: string;
  mode: VncMode;
  expiresInSec: number;
}

export interface PanelCheckDto {
  ok: boolean;
  /** Что получилось: «связь есть, RFB 3.8, пароль принят» или текст ошибки. */
  message: string;
  rfbVersion: string | null;
  security: string | null;
  width: number | null;
  height: number | null;
  desktopName: string | null;
  rttMs: number | null;
}

export interface VncSessionDto {
  id: string;
  panelId: string;
  panelName: string;
  employeeId: string;
  employeeName: string;
  mode: VncMode;
  startedAt: string;
  endedAt: string | null;
  endReason: string | null;
  bytesDown: number;
  bytesUp: number;
  /** Сеанс идёт прямо сейчас (его можно оборвать). */
  live: boolean;
  clicks: number;
  keys: number;
  shots: number;
}

/** Действие в сеансе, записанное сервером из потока команд (подделать с телефона нельзя). */
export interface VncEventDto {
  at: string;
  kind: 'press' | 'drag' | 'key' | 'paste';
  /** Координаты на экране панели (для press — точка нажатия, для drag — где отпустили). */
  x: number | null;
  y: number | null;
  /** Для drag — откуда тянули. */
  fromX: number | null;
  fromY: number | null;
  /** Для key — название клавиши или символ; для paste — сколько символов вставлено. */
  text: string | null;
}

/** Снимок экрана в момент нажатия (делает приложение; точка нажатия отмечена на картинке). */
export interface VncShotDto {
  id: string;
  at: string;
}

export interface VncSessionDetailDto extends VncSessionDto {
  clientIp: string | null;
  screen: { width: number; height: number } | null;
  events: VncEventDto[];
  shotList: VncShotDto[];
}

/* ---------- Проверка входных данных (одна схема на форму и сервер) ---------- */

/** Telegram ID (число) или @username. */
const TgRef = z
  .string()
  .trim()
  .regex(/^(@?[A-Za-z][A-Za-z0-9_]{3,31}|\d{1,15})$/, 'Telegram: числовой ID (например 1014614490) или @username')
  .transform((v) => v.replace(/^@/, ''));

/** Телефон: цифры, приводятся к виду 7XXXXXXXXXX для российских номеров. */
export function normalizePhone(raw: string): string | null {
  let d = raw.replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('8')) d = `7${d.slice(1)}`;
  if (d.length === 10) d = `7${d}`;
  return d.length >= 10 && d.length <= 15 ? d : null;
}

const Phone = z
  .string()
  .trim()
  .refine((v) => normalizePhone(v) !== null, 'Телефон: 10–15 цифр, например +7 916 123-45-67')
  .transform((v) => normalizePhone(v)!);

export const EmployeeInput = z
  .object({
    fullName: z.string().trim().min(3, 'Укажите фамилию и имя').max(120),
    positionId: z.string().uuid().nullable().optional(),
    role: z.enum(ROLES).default('employee'),
    areaIds: z.array(z.string().uuid()).max(200).default([]),
    telegram: TgRef.nullable().optional(),
    phone: Phone.nullable().optional(),
  })
  .refine((v) => v.telegram || v.phone, { message: 'Укажите Telegram (ID или @username) или телефон — по ним сотрудник войдёт', path: ['telegram'] });
export type EmployeeInput = z.input<typeof EmployeeInput>;

export const EmployeePatch = z.object({
  fullName: z.string().trim().min(3).max(120).optional(),
  positionId: z.string().uuid().nullable().optional(),
  role: z.enum(ROLES).optional(),
  status: z.enum(['active', 'blocked']).optional(),
  areaIds: z.array(z.string().uuid()).max(200).optional(),
  /** Менять можно, пока сотрудник ни разу не входил. */
  telegram: TgRef.nullable().optional(),
  phone: Phone.nullable().optional(),
});
export type EmployeePatch = z.input<typeof EmployeePatch>;

/** Должность — иерархия компании: кто кому подчиняется. Прав не даёт (права — роль). */
export interface PositionDto {
  id: string;
  name: string;
  parentId: string | null;
  sort: number;
  /** Сколько сотрудников на этой должности. */
  count: number;
}

export const PositionInput = z.object({
  name: z.string().trim().min(1, 'Название не может быть пустым').max(80),
  parentId: z.string().uuid().nullable().optional(),
  sort: z.number().int().min(0).max(10000).optional(),
});
export type PositionInput = z.input<typeof PositionInput>;

export const AreaInput = z.object({
  name: z.string().trim().min(1, 'Название не может быть пустым').max(80),
  kind: z.enum(['site', 'shop', 'line', 'cell']).default('cell'),
  parentId: z.string().uuid().nullable().optional(),
  sort: z.number().int().min(0).max(10000).optional(),
});
export type AreaInput = z.input<typeof AreaInput>;

/** Адрес: IP или имя хоста в сети завода. Без схемы, пробелов и пути. */
const HOST_RE = /^[a-zA-Z0-9]([a-zA-Z0-9.-]{0,251}[a-zA-Z0-9])?$/;

export const PanelInput = z.object({
  name: z.string().trim().min(1, 'Название не может быть пустым').max(80),
  description: z.string().trim().max(300).nullable().optional(),
  areaId: z.string().uuid().nullable().optional(),
  host: z.string().trim().regex(HOST_RE, 'Адрес — IP или имя компьютера, например 192.168.1.20'),
  port: z.number().int().min(1, 'Порт от 1 до 65535').max(65535, 'Порт от 1 до 65535'),
  /** Строка — задать пароль, null — убрать, не передавать — оставить как есть. */
  password: z.string().max(64).nullable().optional(),
  controlAllowed: z.boolean().optional(),
  enabled: z.boolean().optional(),
  sort: z.number().int().min(0).max(10000).optional(),
  /** Единица оборудования; null — отвязать. */
  equipmentId: z.string().uuid().nullable().optional(),
  side: z.enum(['front', 'back', 'left', 'right']).nullable().optional(),
});
export type PanelInput = z.input<typeof PanelInput>;

/* ---------- Оборудование ---------- */

const TRANSLIT: Record<string, string> = {
  а: 'A', б: 'B', в: 'V', г: 'G', д: 'D', е: 'E', ё: 'E', ж: 'ZH', з: 'Z', и: 'I', й: 'Y', к: 'K', л: 'L', м: 'M', н: 'N', о: 'O', п: 'P',
  р: 'R', с: 'S', т: 'T', у: 'U', ф: 'F', х: 'H', ц: 'C', ч: 'CH', ш: 'SH', щ: 'SCH', ъ: '', ы: 'Y', ь: '', э: 'E', ю: 'YU', я: 'YA',
};

/**
 * Ключ QR-кода из заводского номера: «ШТ.20.01.01.02.000» → «S_20_01_01_02_000».
 * Так устроены коды на оборудовании: t.me/<бот>?start=<ключ> (или ?startapp=<ключ> — сразу в приложение).
 * Telegram разрешает в ключе только латиницу, цифры, _ и - (до 64 символов).
 */
export function qrKeyFromCode(code: string): string {
  const s = code
    .trim()
    .replace(/^шт(?=[^а-яё]|$)/i, 'S')
    .toLowerCase()
    .split('')
    .map((ch) => TRANSLIT[ch] ?? ch)
    .join('');
  return s
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase()
    .slice(0, 64);
}

/** Обратно, для подсказки при заведении по отсканированному коду: «S_20_01_01_02_000» → «ШТ.20.01.01.02.000». */
export function codeFromQrKey(qr: string): string {
  const m = /^S_(.+)$/i.exec(qr);
  return m ? `ШТ.${m[1]!.replace(/_/g, '.')}` : qr;
}

export const QR_KEY_RE = /^[A-Za-z0-9_-]{1,64}$/;

export type EquipmentFileKind = 'photo' | 'video' | 'doc';
export const FILE_KIND_LABEL: Record<EquipmentFileKind, string> = { photo: 'Фото', video: 'Видео', doc: 'Документы' };

/** Какие файлы принимаем и как показываем. */
export const FILE_TYPES: Record<string, EquipmentFileKind> = {
  'image/jpeg': 'photo',
  'image/png': 'photo',
  'image/webp': 'photo',
  'image/gif': 'photo',
  'video/mp4': 'video',
  'video/quicktime': 'video',
  'video/webm': 'video',
  'application/pdf': 'doc',
};

export interface EquipmentFileDto {
  id: string;
  /** Файл прикреплён к пункту списка действий — в общей галерее не показывается. */
  step: boolean;
  kind: EquipmentFileKind;
  title: string;
  mime: string;
  size: number;
  createdAt: string;
  /** Путь в API (без /api). Не ссылка: файл отдаётся только с авторизацией, приложение показывает его из памяти. */
  url: string;
}

/** Единица оборудования в списке. */
export interface EquipmentDto {
  id: string;
  areaId: string | null;
  /** Заводской номер, например ШТ.20.01.01.02.000. */
  code: string;
  /** Ключ QR-кода, например S_20_01_01_02_000. */
  qr: string;
  name: string;
  sort: number;
  /** Панели этой единицы (только те, что видит пользователь). */
  panelIds: string[];
  fileCount: number;
  /** Главное фото — путь в API (с авторизацией, см. EquipmentFileDto.url); null — фото нет. */
  avatarUrl: string | null;
  avatarFileId: string | null;
  /** Этапы-проверки с пунктами (например, «Перед началом работы») — их проходят с галочками. */
  checkProcedures: { id: string; title: string }[];
}

/** Страница единицы оборудования. */
export interface EquipmentDetailDto extends EquipmentDto {
  /** Списки действий по этапам (пустые этапы не приходят). */
  procedures: Procedure[];
  /** «Ответственный за чистоту и порядок». */
  responsible: string | null;
  /** Проверки за сегодня (новые сверху). */
  checks: CheckDto[];
  panels: PanelDto[];
  files: EquipmentFileDto[];
  /** Ссылки для QR-кода: в чат бота и сразу в приложение. */
  links: { bot: string | null; app: string | null };
}

export const EquipmentInput = z.object({
  name: z.string().trim().min(1, 'Укажите название').max(120),
  code: z.string().trim().min(1, 'Укажите заводской номер').max(60),
  /** Ключ QR; не задан — из заводского номера. */
  qr: z.string().trim().regex(QR_KEY_RE, 'Ключ QR: латиница, цифры, _ и -, до 64 символов').nullable().optional(),
  areaId: z.string().uuid().nullable().optional(),
  description: z.string().trim().max(5000).nullable().optional(),
  instruction: z.string().trim().max(20000).nullable().optional(),
  sort: z.number().int().min(0).max(10000).optional(),
  /** Какие панели относятся к этой единице. */
  panelIds: z.array(z.string().uuid()).max(50).optional(),
  /** Главное фото — одно из фото этой единицы; null — убрать. */
  avatarFileId: z.string().uuid().nullable().optional(),
  procedures: ProceduresInput.optional(),
  responsible: z.string().trim().max(120).nullable().optional(),
});

/** Порядок в списке: id единиц в нужном порядке (сверху вниз). */
export const EquipmentOrder = z.object({ ids: z.array(z.string().uuid()).min(1).max(2000) });
export type EquipmentInput = z.input<typeof EquipmentInput>;
